// src/components/ExportOptionsModal.tsx

import { useState, useEffect } from 'react';
import { X, Lock } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { getPlanFeatures, FREE_CSV_COLUMNS } from '@/lib/planFeatures';
import { UpgradeModal } from '@/components/UpgradeModal';
import type { Project } from '@/types';

export interface ExportColumn {
  key: string;
  label: string;
  checked: boolean;
  disabled: boolean;
  locked?: boolean;
}

interface ExportOptionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExport: (selectedColumns: string[], selectedProject?: string) => void;
  exportType: 'csv' | 'pdf';
  projects: Project[];
}

export function ExportOptionsModal({
  isOpen,
  onClose,
  onExport,
  exportType,
  projects,
}: ExportOptionsModalProps) {
  const { user } = useAuth();
  const features = getPlanFeatures(user);
  
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [columns, setColumns] = useState<ExportColumn[]>([]);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState('');

  // PDFエクスポートが利用可能か
  const canExportPdf = features.pdfExport;

  useEffect(() => {
    const canExportFullCsv = features.csvFullColumns;
    
    const defaultColumns: ExportColumn[] = [
      { key: 'date', label: '日付', checked: true, disabled: true },
      { key: 'project', label: 'プロジェクト名', checked: true, disabled: true },
      { key: 'startTime', label: '出勤', checked: true, disabled: false },
      { key: 'endTime', label: '退勤', checked: true, disabled: false },
      { 
        key: 'actualHours', 
        label: '実稼働(h)', 
        checked: canExportFullCsv && exportType === 'csv', 
        disabled: false,
        locked: !canExportFullCsv,
      },
      { 
        key: 'performance', 
        label: '実績(h)', 
        checked: canExportFullCsv, 
        disabled: false,
        locked: !canExportFullCsv,
      },
      { 
        key: 'category', 
        label: 'カテゴリ', 
        checked: canExportFullCsv && exportType === 'csv', 
        disabled: false,
        locked: !canExportFullCsv,
      },
      { 
        key: 'memo', 
        label: '備考', 
        checked: canExportFullCsv && exportType === 'csv', 
        disabled: false,
        locked: !canExportFullCsv,
      },
    ];

    setColumns(defaultColumns);

    if (exportType === 'pdf' && projects.length > 0) {
      setSelectedProject(projects[0].id);
    }
  }, [exportType, projects, features.csvFullColumns]);

  const handleColumnToggle = (key: string) => {
    const column = columns.find((col) => col.key === key);
    
    if (column?.locked) {
      setUpgradeFeature('CSV全項目エクスポート');
      setShowUpgradeModal(true);
      return;
    }
    
    setColumns(
      columns.map((col) =>
        col.key === key && !col.disabled ? { ...col, checked: !col.checked } : col
      )
    );
  };

  const handleExport = () => {
    let selectedColumns = columns.filter((col) => col.checked).map((col) => col.key);
    
    if (!features.csvFullColumns) {
      selectedColumns = selectedColumns.filter((key) => FREE_CSV_COLUMNS.includes(key));
    }
    
    onExport(selectedColumns, exportType === 'pdf' ? selectedProject : undefined);
    onClose();
  };

  const handlePdfExport = () => {
    if (!canExportPdf) {
      setUpgradeFeature('PDFエクスポート');
      setShowUpgradeModal(true);
      return;
    }
    handleExport();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
          {/* ヘッダー */}
          <div className="border-b px-6 py-4 flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">出力オプション</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* 内容 */}
          <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
            {/* Freeプランの場合の注意表示（CSV時のみ） */}
            {!features.csvFullColumns && exportType === 'csv' && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800">
                  <strong>Freeプラン:</strong> 出力項目が制限されています。
                  Standardプランにアップグレードすると、全項目を出力できます。
                </p>
              </div>
            )}

            {/* PDF時のFreeプラン注意表示 */}
            {!canExportPdf && exportType === 'pdf' && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <Lock className="w-5 h-5 text-amber-600" />
                  <p className="text-sm text-amber-800">
                    <strong>Freeプラン:</strong> PDFエクスポートはStandardプラン限定機能です。
                  </p>
                </div>
              </div>
            )}

            {/* PDF用プロジェクト選択 */}
            {exportType === 'pdf' && (
              <div className={!canExportPdf ? 'opacity-50 pointer-events-none' : ''}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  プロジェクト
                </label>
                <select
                  value={selectedProject}
                  onChange={(e) => setSelectedProject(e.target.value)}
                  disabled={!canExportPdf}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-100"
                >
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.code ? `${project.code} - ` : ''}
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* 出力項目選択 */}
            <div className={exportType === 'pdf' && !canExportPdf ? 'opacity-50 pointer-events-none' : ''}>
              <p className="text-sm font-medium text-gray-700 mb-3">出力項目</p>
              <div className="space-y-2">
                {columns.map((column) => (
                  <label
                    key={column.key}
                    className={`flex items-center justify-between p-2 rounded-lg ${
                      column.disabled 
                        ? 'cursor-not-allowed opacity-50' 
                        : column.locked 
                        ? 'cursor-pointer bg-gray-50 hover:bg-gray-100' 
                        : 'cursor-pointer hover:bg-gray-50'
                    }`}
                    onClick={(e) => {
                      if (column.locked) {
                        e.preventDefault();
                        handleColumnToggle(column.key);
                      }
                    }}
                  >
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={column.checked}
                        disabled={column.disabled || column.locked}
                        onChange={() => handleColumnToggle(column.key)}
                        className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                      <span className={`text-sm ${column.locked ? 'text-gray-400' : 'text-gray-700'}`}>
                        {column.label}
                      </span>
                    </div>
                    {column.locked && (
                      <Lock className="w-4 h-4 text-gray-400" />
                    )}
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* ボタン */}
          <div className="border-t px-6 py-4 flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              キャンセル
            </button>
            
            {exportType === 'pdf' ? (
              // PDFボタン（Freeプランはグレーアウト + ロックアイコン）
              <button
                onClick={handlePdfExport}
                className={`px-6 py-2 rounded-lg font-medium transition-colors shadow-md flex items-center gap-2 ${
                  canExportPdf
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-gray-300 text-gray-500 cursor-pointer hover:bg-gray-400'
                }`}
              >
                {!canExportPdf && <Lock className="w-4 h-4" />}
                実行
              </button>
            ) : (
              // CSVボタン
              <button
                onClick={handleExport}
                className="px-6 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors shadow-md"
              >
                実行
              </button>
            )}
          </div>
        </div>
      </div>

      {/* アップグレードモーダル */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature={upgradeFeature}
      />
    </>
  );
}
